import athenaService from './athenaService';

class PatientService {
  static async getPatients() {
    try {
      // Attempt to get patients from Athena API via booked appointments
      console.log('Fetching patients from Athena API...');
      const appointments = await athenaService.getBookedAppointments();
      
      if (appointments && Array.isArray(appointments) && appointments.length > 0) {
        // Extract unique patients from appointments
        const patientMap = new Map();
        
        appointments.forEach(appt => {
          if (appt.patient && appt.patientid) {
            const patient = this.formatAthenaPatient(appt.patient, appt.patientid);
            console.log('Extracted patient:', patient);
            // Only include active patients
            if (patient.status !== 'inactive') {
              patientMap.set(appt.patientid, patient);
            }
          }
        });
        
        const patients = Array.from(patientMap.values());
        console.log(`Successfully extracted ${patients.length} active patients from appointments`);
        return patients;
      }
      
      // Fall back to localStorage if API fails or returns empty
      console.log('No patients from Athena API, falling back to localStorage');
      const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
      // Filter out inactive patients
      return localPatients.filter(patient => patient.status !== 'inactive');
    } catch (error) {
      console.error('Error fetching patients from API:', error);
      // Fallback to localStorage
      const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
      // Filter out inactive patients
      return localPatients.filter(patient => patient.status !== 'inactive');
    }
  }

  static formatAthenaPatient(athenaPatient, patientId) {
    return {
      id: patientId || athenaPatient.patientid,
      firstName: athenaPatient.firstname || '',
      lastName: athenaPatient.lastname || '',
      dateOfBirth: athenaPatient.dob || '',
      email: athenaPatient.email || '',
      phone: athenaPatient.mobilephone || athenaPatient.homephone || '',
      address: athenaPatient.address1 || '',
      city: athenaPatient.city || '',
      state: athenaPatient.state || '',
      zipCode: athenaPatient.zip || '',
      isNew: false,
      createdAt: new Date().toISOString(),
      status: athenaPatient.status || 'active' // Default to active if not specified
    };
  }

  static async getPatientById(id) {
    try {
      // Search for the patient in Athena API
      const patients = await this.getPatients();
      return patients.find(patient => patient.id === id);
    } catch (error) {
      console.error('Error fetching patient by ID from API:', error);
      // Fall back to localStorage
      const patients = JSON.parse(localStorage.getItem('patients') || '[]');
      return patients.find(patient => patient.id === id);
    }
  }

  static async savePatient(patientData) {
    try {
      // Try to create the patient in Athena API
      console.log('Creating patient in Athena API:', patientData);
      const athenaPatient = await athenaService.createPatient(patientData);
      
      if (athenaPatient) {
        // Format the result
        const formattedPatient = this.formatAthenaPatient(athenaPatient, athenaPatient.patientid);
        console.log('Successfully created patient in Athena API:', formattedPatient);
        return formattedPatient;
      }
      
      // Fall back to localStorage if API fails
      console.log('Failed to create patient in Athena API, falling back to localStorage');
      const patients = JSON.parse(localStorage.getItem('patients') || '[]');
      const newPatient = {
        id: Date.now(),
        ...patientData,
        createdAt: new Date().toISOString()
      };
      patients.push(newPatient);
      localStorage.setItem('patients', JSON.stringify(patients));
      return newPatient;
    } catch (error) {
      console.error('Error creating patient in Athena API:', error);
      // Fall back to localStorage
      const patients = JSON.parse(localStorage.getItem('patients') || '[]');
      const newPatient = {
        id: Date.now(),
        ...patientData,
        createdAt: new Date().toISOString()
      };
      patients.push(newPatient);
      localStorage.setItem('patients', JSON.stringify(patients));
      return newPatient;
    }
  }

  static async updatePatient(id, updateData) {
    try {
      // Try to update the patient in Athena API first
      console.log('Updating patient in Athena API:', id, updateData);
      const athenaPatient = await athenaService.updatePatient(id, updateData);
      
      if (athenaPatient) {
        // Format the result
        const formattedPatient = this.formatAthenaPatient(athenaPatient, athenaPatient.patientid);
        console.log('Successfully updated patient in Athena API:', formattedPatient);
        return formattedPatient;
      }
      
      // Fall back to localStorage if API fails
      console.log('Failed to update patient in Athena API, falling back to localStorage');
      const patients = JSON.parse(localStorage.getItem('patients') || '[]');
      const index = patients.findIndex(patient => patient.id === id);
      
      if (index !== -1) {
        patients[index] = {
          ...patients[index],
          ...updateData
        };
        localStorage.setItem('patients', JSON.stringify(patients));
        return patients[index];
      }
      
      return null;
    } catch (error) {
      console.error('Error updating patient in Athena API:', error);
      // Fall back to localStorage
      const patients = JSON.parse(localStorage.getItem('patients') || '[]');
      const index = patients.findIndex(patient => patient.id === id);
      
      if (index !== -1) {
        patients[index] = {
          ...patients[index],
          ...updateData
        };
        localStorage.setItem('patients', JSON.stringify(patients));
        return patients[index];
      }
      
      return null;
    }
  }

  static async deletePatient(id) {
    try {
      // Try to archive the patient in Athena API first
      console.log('Archiving patient in Athena API:', id);
      const response = await athenaService.archivePatient(id);
      
      if (response) {
        console.log('Successfully archived patient in Athena API:', response);
        return true;
      }
      
      // Fall back to localStorage if API fails
      console.log('Failed to archive patient in Athena API, falling back to localStorage');
      const patients = JSON.parse(localStorage.getItem('patients') || '[]');
      const filteredPatients = patients.filter(patient => patient.id !== id);
      
      if (filteredPatients.length < patients.length) {
        localStorage.setItem('patients', JSON.stringify(filteredPatients));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error archiving patient in Athena API:', error);
      // Fall back to localStorage
      const patients = JSON.parse(localStorage.getItem('patients') || '[]');
      const filteredPatients = patients.filter(patient => patient.id !== id);
      
      if (filteredPatients.length < patients.length) {
        localStorage.setItem('patients', JSON.stringify(filteredPatients));
        return true;
      }
      
      return false;
    }
  }

  // Restore a previously archived patient
  static async restorePatient(id) {
    try {
      // Try to restore the patient in Athena API first
      console.log('Restoring patient in Athena API:', id);
      const response = await athenaService.restorePatient(id);
      
      if (response) {
        console.log('Successfully restored patient in Athena API:', response);
        return response;
      }
      
      // Return false if API call fails
      console.log('Failed to restore patient in Athena API');
      return false;
    } catch (error) {
      console.error('Error restoring patient in Athena API:', error);
      return false;
    }
  }

  // Method to get archived patients
  static async getArchivedPatients(departmentId, providerId) {
    try {
      // Get all patients including inactive ones from Athena API
      console.log('Fetching archived patients from Athena API with filters:', { departmentId, providerId });
      
      // Use a 30-day lookback period for appointments
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - 30);
      const startDate = pastDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      
      // Use a 30-day future period
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      const endDate = futureDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });

      // Create options with the same structure as getPatients
      const options = {
        scheduledstartdate: startDate,
        scheduledenddate: endDate,
        departmentId: departmentId === '' ? 1 : parseInt(departmentId), // Default to department 1
        showpatientdetail: true,
        limit: 100 // Higher limit to get more patients
      };
      
      // Add provider filter if a specific provider is selected
      if (providerId && providerId !== '') {
        options.providerId = parseInt(providerId);
      }
      
      const allAppointments = await athenaService.getBookedAppointments(options);
      
      if (allAppointments && Array.isArray(allAppointments) && allAppointments.length > 0) {
        // Extract unique patients with inactive status
        const patientMap = new Map();
        
        allAppointments.forEach(appt => {
          if (appt.patient && appt.patient.patientid && appt.patient.status === 'inactive') {
            const patientId = appt.patient.patientid;
            
            // Create a formatted patient object similar to active patients
            const formattedPatient = {
              id: patientId,
              firstName: appt.patient.firstname || '',
              lastName: appt.patient.lastname || '',
              email: appt.patient.email || '',
              phone: appt.patient.mobilephone || appt.patient.homephone || '',
              dob: appt.patient.dob || '',
              sex: appt.patient.sex || '',
              address: appt.patient.address1 || '',
              city: appt.patient.city || '',
              state: appt.patient.state || '',
              zip: appt.patient.zip || '',
              lastAppointment: appt.patient.lastappointment || '',
              departmentId: appt.departmentid || appt.patient.departmentid || 1,
              providerId: appt.providerid || appt.patient.primaryproviderid || '',
              patientData: appt.patient,
              status: 'inactive'
            };
            
            patientMap.set(patientId, formattedPatient);
          }
        });
        
        const archivedPatients = Array.from(patientMap.values());
        console.log(`Found ${archivedPatients.length} archived patients from API`);
        return archivedPatients;
      }
      
      // Fall back to localStorage if API fails or returns empty
      console.log('No archived patients from Athena API, checking localStorage');
      const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
      return localPatients.filter(patient => patient.status === 'inactive');
    } catch (error) {
      console.error('Error fetching archived patients from API:', error);
      // Fallback to localStorage
      const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
      return localPatients.filter(patient => patient.status === 'inactive');
    }
  }

  // Get patient's appointment history
  static getPatientAppointments(patientId) {
    const appointments = JSON.parse(localStorage.getItem('appointments') || '[]');
    return appointments.filter(apt => apt.patientId === patientId);
  }

  // Search patients by any field
  static searchPatients(query) {
    const patients = this.getPatients();
    const searchTerm = query.toLowerCase();
    return patients.filter(patient => 
      patient.name?.toLowerCase().includes(searchTerm) ||
      patient.email?.toLowerCase().includes(searchTerm) ||
      patient.phone?.includes(searchTerm) ||
      patient.notes?.toLowerCase().includes(searchTerm)
    );
  }
}

export default PatientService;
